import { useTranslation } from 'react-i18next';
import BladeSimpleSection from '@/components/ui/BladeSimpleSection/BladeSimpleSection';
import { CURRENCY_BY_CODE, UNLIMITED_PAYMENTS, formatDate, formatMoney, parseDate } from '../standingOrderModel';
import { StandingOrderActionLogEntry, parseAutoFreezeDetails, parseEditDetails } from '../standingOrderActions';
import styles from './AdditionalInfo.module.css';

interface OrderActionLogProps {
  entries: StandingOrderActionLogEntry[] | undefined;
}

// 'create': a bank order set up from the board (HAT-1478) - its first line.
const KNOWN_ACTIONS = new Set(['create', 'charge', 'freeze', 'reactivate', 'edit', 'cancel', 'auto-freeze']);

/**
 * "פעולות שבוצעו מהאתר": what was done to this order from the board, and by whom. Nedarim's own
 * history of the order knows only that the mosad's key was used, and on the shared mosadot that
 * key belongs to every fund - this is the only place that says which coordinator froze it.
 *
 * Shown only when there is something in it, so an order nobody has touched gets no empty panel.
 * A refusal is listed too, in Nedarim's words: it is exactly what someone asks about later.
 */
const OrderActionLog = ({ entries }: OrderActionLogProps) => {
  const { t, i18n } = useTranslation();
  const prefix = 'standingOrders.orderActions.log';

  const rows = (entries ?? []).filter((entry) => KNOWN_ACTIONS.has(entry.action));
  if (rows.length === 0) return null;

  const money = (value: number | null | undefined, currency: number) =>
    value == null ? '' : formatMoney(value, CURRENCY_BY_CODE[currency === 2 ? 2 : 1], i18n.language);

  const describeEdit = (entry: StandingOrderActionLogEntry): string => {
    const details = parseEditDetails(entry.details);
    const parts: string[] = [];
    if (details.amount) {
      parts.push(t(`${prefix}.changeAmount`, { from: money(details.amount[0], entry.currency), to: money(details.amount[1], entry.currency) }));
    }
    if (details.nextDate?.[1]) {
      const date = parseDate(details.nextDate[1]);
      parts.push(t(`${prefix}.changeNextDate`, { date: date ? formatDate(date, i18n.language) : details.nextDate[1] }));
    }
    if (details.remainingPayments?.[1] != null) {
      parts.push(details.remainingPayments[1] >= UNLIMITED_PAYMENTS
        ? t(`${prefix}.changeUnlimited`)
        : t(`${prefix}.changePayments`, { count: details.remainingPayments[1] }));
    }
    if (details.comments) parts.push(t(`${prefix}.changeComments`));
    return parts.join(' · ');
  };

  return (
    <BladeSimpleSection title={t(`${prefix}.title`)}>
      <ul className={styles.actionLog}>
        {rows.map((entry, index) => {
          const at = new Date(entry.requestedAt);
          const isAutoFreeze = entry.action === 'auto-freeze';
          const label = entry.action === 'charge'
            ? t(`${prefix}.charge`, { amount: money(entry.amount, entry.currency) })
            : isAutoFreeze
              ? t(`${prefix}.autoFreeze`, { count: parseAutoFreezeDetails(entry.details).count })
              : t(`${prefix}.${entry.action}`);
          const edit = entry.action === 'edit' && entry.succeeded ? describeEdit(entry) : '';
          // HAT-1441: the reason a bank order was frozen or cancelled with.
          const reason = (entry.action === 'freeze' || entry.action === 'cancel') ? parseEditDetails(entry.details).reason?.[1] : null;
          // HAT-1447: the declined-orders check froze it; there is no person to name.
          const by = isAutoFreeze ? t(`${prefix}.system`) : entry.requestedBy;

          return (
            <li key={`${entry.requestedAt}-${index}`} className={styles.actionLogRow}>
              {/* Separate items rather than one joined string: a date and a Latin name in a
                  Hebrew line reorder themselves when bidi gets to place the separators. */}
              <span className={styles.actionLogMain}>
                <span dir="ltr" className={styles.actionLogDate}>{isNaN(at.getTime()) ? '' : formatDate(at, i18n.language)}</span>
                <span aria-hidden="true" className={styles.actionLogSep}>·</span>
                <span>{label}</span>
                {by && (
                  <>
                    <span aria-hidden="true" className={styles.actionLogSep}>·</span>
                    <bdi className={styles.actionLogBy}>{by}</bdi>
                  </>
                )}
              </span>
              {edit && <span className={styles.actionLogDetail}>{edit}</span>}
              {reason && <span className={styles.actionLogDetail}>{t('standingOrders.orderActions.bank.reason.shown', { reason })}</span>}
              {!entry.succeeded && (
                <span className={styles.actionLogRefused}>
                  {t(`${prefix}.refused`, { message: entry.message ?? '' })}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </BladeSimpleSection>
  );
};

export default OrderActionLog;
